import { useCallback, useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { dayjs } from "@thalamos/common";
import type {
  ConfirmSection12PhoneRequest,
  Section12PhoneVerificationStatus,
  SendSection12PhoneVerificationCodeRequest,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import {
  Button,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { formatToE164, isValidE164 } from "~/utils/phone-utils.js";

/**
 * Generates a random 6-digit verification code
 */
function generateVerificationCode(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

interface PhoneVerificationSectionProps {
  userId: string;
  onPhoneVerified?: () => void;
}

type VerificationStep = "input" | "code-sent" | "verified";

export function PhoneVerificationSection({
  userId,
  onPhoneVerified,
}: PhoneVerificationSectionProps) {
  const [phoneStatus, setPhoneStatus] =
    useState<Section12PhoneVerificationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [phoneInput, setPhoneInput] = useState("");
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState<VerificationStep>("input");
  const [sentCode, setSentCode] = useState<string | null>(null);
  const [enteredCode, setEnteredCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Fetch current phone status
  const fetchPhoneStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.section12.getPhoneStatus(userId);
      setPhoneStatus(response.data);

      // If already verified, show that state
      if (response.data.phoneNumber && response.data.verifiedAt) {
        setPhoneInput(response.data.phoneNumber);
        setConsent(response.data.consentGiven);
        setStep("verified");
      }
    } catch (err) {
      console.error("Failed to fetch phone status:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void fetchPhoneStatus();
  }, [fetchPhoneStatus]);

  // Handle sending verification code
  const handleSendCode = async () => {
    setError(null);

    // Validate phone number
    const formattedPhone = formatToE164(phoneInput);
    if (!isValidE164(formattedPhone)) {
      setError(
        "Please enter a valid phone number (e.g., 07700 900000 or +447700900000)",
      );
      return;
    }

    // Validate consent
    if (!consent) {
      setError("You must consent to AMHPs contacting you at this number");
      return;
    }

    setSaving(true);
    try {
      const code = generateVerificationCode();
      const request: SendSection12PhoneVerificationCodeRequest = {
        phoneNumber: formattedPhone,
        code,
        consent,
      };

      await api.section12.sendPhoneVerificationCode(userId, request);

      setSentCode(code);
      setPhoneInput(formattedPhone);
      setStep("code-sent");
      renderSuccessToast({ message: "Verification code sent via SMS" });
    } catch (err) {
      console.error("Failed to send verification code:", err);
      setError("Failed to send verification code. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle code verification
  const handleVerifyCode = async () => {
    setError(null);

    // Verify code matches
    if (enteredCode !== sentCode) {
      setError("The code you entered does not match. Please try again.");
      return;
    }

    setSaving(true);
    try {
      const request: ConfirmSection12PhoneRequest = {
        phoneNumber: phoneInput,
        consent,
      };

      await api.section12.confirmPhone(userId, request);

      setStep("verified");
      renderSuccessToast({ message: "Phone number verified successfully" });
      onPhoneVerified?.();

      // Refresh status
      await fetchPhoneStatus();
    } catch (err) {
      console.error("Failed to confirm phone:", err);
      setError("Failed to save phone number. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle removing phone number
  const handleRemovePhone = async () => {
    setSaving(true);
    try {
      await api.section12.removePhone(userId);

      setPhoneInput("");
      setConsent(false);
      setSentCode(null);
      setEnteredCode("");
      setStep("input");
      setPhoneStatus(null);
      renderSuccessToast({ message: "Phone number removed" });
    } catch (err) {
      console.error("Failed to remove phone:", err);
      renderErrorToast({ message: "Failed to remove phone number" });
    } finally {
      setSaving(false);
    }
  };

  // Handle changing phone number
  const handleChangePhone = () => {
    setStep("input");
    setSentCode(null);
    setEnteredCode("");
    setError(null);
  };

  // Handle resending code
  const handleResendCode = async () => {
    setError(null);
    setSaving(true);

    try {
      const code = generateVerificationCode();
      const request: SendSection12PhoneVerificationCodeRequest = {
        phoneNumber: phoneInput,
        code,
        consent,
      };

      await api.section12.sendPhoneVerificationCode(userId, request);

      setSentCode(code);
      setEnteredCode("");
      renderSuccessToast({ message: "New verification code sent" });
    } catch (err) {
      console.error("Failed to resend verification code:", err);
      setError("Failed to send verification code. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }} data-testid="section12-phone-section">
        <Typography variant="h6" gutterBottom>
          Phone Number for AMHP Contact
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          Loading...
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }} data-testid="section12-phone-section">
      <Typography variant="h6" gutterBottom>
        Phone Number for AMHP Contact
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        Provide a phone number where AMHPs can contact you about Section 12
        requests
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {step === "verified" && phoneStatus?.phoneNumber && (
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "success.50",
              border: 1,
              borderColor: "success.200",
            }}
          >
            <CheckCircleIcon color="success" fontSize="small" />
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flex: 1 }}
            >
              <Typography variant="body2" fontWeight={600}>
                {phoneStatus.phoneNumber}
              </Typography>
              {phoneStatus.verifiedAt && (
                <Typography variant="caption" color="text.secondary">
                  — verified on{" "}
                  {dayjs(phoneStatus.verifiedAt).format("D MMMM YYYY")}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="text"
                size="small"
                label="Change Phone Number"
                onClick={handleChangePhone}
                disabled={saving}
                testId="section12-phone-change-btn"
              />
              <Button
                variant="text"
                size="small"
                color="error"
                label="Remove Phone Number"
                onClick={() => void handleRemovePhone()}
                disabled={saving}
                testId="section12-phone-remove-btn"
                sx={{ "&:hover": { color: "error.main" } }}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
      {step === "input" && (
        <Stack spacing={2}>
          <TextField
            label="Phone Number"
            placeholder="07700 900000"
            value={phoneInput}
            onChange={(e) => {
              setPhoneInput(e.target.value);
            }}
            size="small"
            sx={{ maxWidth: 320 }}
            helperText="Enter your UK mobile number (e.g., 07700 900000 or +447700900000)"
            data-testid="section12-phone-input"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={consent}
                onChange={(e) => {
                  setConsent(e.target.checked);
                }}
                size="small"
                data-testid="section12-phone-consent-checkbox"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I consent to AMHPs contacting me at this phone number regarding
                Section 12 assessment requests
              </Typography>
            }
            sx={{ alignItems: "center", ml: -0.5 }}
          />

          <Box>
            <Button
              variant="contained"
              size="small"
              label={saving ? "Sending..." : "Send Verification Code"}
              onClick={() => void handleSendCode()}
              disabled={saving || !phoneInput || !consent}
              testId="section12-phone-send-code-btn"
            />
          </Box>
        </Stack>
      )}
      {step === "code-sent" && (
        <Stack spacing={2}>
          <Alert severity="info" sx={{ py: 0.5 }}>
            <Typography variant="body2">
              We&apos;ve sent a 6-digit verification code to{" "}
              <strong>{phoneInput}</strong>. Please enter the code below to
              verify your phone number.
            </Typography>
          </Alert>

          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <TextField
              label="Verification Code"
              placeholder="000000"
              value={enteredCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                setEnteredCode(value);
              }}
              size="small"
              sx={{ width: 180 }}
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                  style: {
                    letterSpacing: "0.3em",
                    fontSize: "1.1em",
                    textAlign: "center",
                    fontFamily: "monospace",
                  },
                },
              }}
              data-testid="section12-phone-code-input"
            />
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Button
                variant="contained"
                size="small"
                label={saving ? "Verifying..." : "Verify Code"}
                onClick={() => void handleVerifyCode()}
                disabled={saving || enteredCode.length !== 6}
                testId="section12-phone-verify-btn"
              />
              <Button
                variant="outlined"
                size="small"
                label="Resend Code"
                onClick={() => void handleResendCode()}
                disabled={saving}
                testId="section12-phone-resend-btn"
              />
              <Button
                variant="text"
                size="small"
                label="Change Number"
                onClick={handleChangePhone}
                disabled={saving}
                testId="section12-phone-back-btn"
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}
